<template>
    <PopupTemplate
        v-model="isOpen"
        :img-raw="illustrationSvgRaw"
        :img-alt="t('vue.free_plan_popup_img_alt')"
        :title="titleText"
        @close="handlePopupClose"
    >
        <template #body>
            <div class="free-plan-popup-body">
                <p class="free-plan-popup-description">
                    {{ descriptionText }}
                </p>

                <select
                    v-if="requiresDomainSelection"
                    v-model="selectedDomain"
                    class="free-plan-popup-select"
                    :disabled="isLoadingDomains || isSubmitting"
                >
                    <option value="" disabled>
                        {{ t("vue.free_plan_popup_select_domain") }}
                    </option>
                    <option v-if="isLoadingDomains" value="" disabled>
                        Loading domains...
                    </option>
                    <option
                        v-if="!isLoadingDomains && domains.length === 0"
                        value=""
                        disabled
                    >
                        No domains available
                    </option>
                    <option v-for="d in domains" :key="d" :value="d">
                        {{ d }}
                    </option>
                </select>

                <div
                    v-if="requiresDomainSelection"
                    class="free-plan-popup-warning"
                >
                    <p class="free-plan-popup-warning-text">
                        {{ t("vue.free_plan_popup_note") }}
                    </p>
                </div>
            </div>
        </template>

        <template #actions>
            <div class="free-plan-popup-actions">
                <AtomicButton
                    color="light-blue"
                    size="lg"
                    :disabled="isSubmitting"
                    @click="close"
                >
                    {{ t("vue.cancel") }}
                </AtomicButton>

                <AtomicButton
                    color="yellow"
                    size="lg"
                    class="free-plan-popup-confirm"
                    :disabled="isConfirmDisabled"
                    @click="confirm"
                >
                    {{ confirmLabel }}
                </AtomicButton>
            </div>
        </template>
    </PopupTemplate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import axios from "axios";
import route from "ziggy-js";

import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import PopupTemplate from "@/components/Atomic/Dialogs/PopupTemplate.vue";
import { useTranslations } from "@/composables/useTranslations";
import { create } from "@/composables/useNotifications";
import freePlanContinueIllustrationRaw from "@/assets/images/free-plan-continue.svg?raw";
import freePlanDowngradeIllustrationRaw from "@/assets/images/free-plan-downgrade.svg?raw";
import freePlanUpgradeIllustrationRaw from "@/assets/images/free-plan-upgrade.svg?raw";

const props = withDefaults(
    defineProps<{
        modelValue: boolean;
        mode: "downgrade" | "continue";
    }>(),
    {},
);

const emit = defineEmits<{
    (e: "update:modelValue", value: boolean): void;
    (e: "close"): void;
    (e: "success", mode: "downgrade" | "continue"): void;
}>();

const { t } = useTranslations();
const selectedDomain = ref("");
const domains = ref<string[]>([]);
const domainsAbsoluteCount = ref(0);
const isLoadingDomains = ref(false);
const isSubmitting = ref(false);

const isOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit("update:modelValue", value),
});

const titleText = computed(() =>
    props.mode === "downgrade"
        ? t("vue.free_plan_popup_title_downgrade")
        : t("vue.free_plan_popup_title_continue"),
);

const confirmLabel = computed(() =>
    props.mode === "downgrade"
        ? t("vue.free_plan_popup_confirm_downgrade")
        : t("vue.free_plan_popup_confirm_switch"),
);

const remainingDomainsCount = computed(() =>
    Math.max(domainsAbsoluteCount.value - 1, 0),
);
const requiresDomainSelection = computed(() => domainsAbsoluteCount.value > 1);
const descriptionText = computed(() =>
    requiresDomainSelection.value
        ? t("vue.free_plan_popup_body", {
              count: remainingDomainsCount.value,
          })
        : t("vue.free_plan_subtitle"),
);

const isConfirmDisabled = computed(() => {
    if (isLoadingDomains.value || isSubmitting.value) return true;
    if (requiresDomainSelection.value) {
        return !selectedDomain.value || domains.value.length === 0;
    }
    return false;
});

const illustrationSvgRaw = computed(() => {
    if (props.mode === "continue") return freePlanContinueIllustrationRaw;
    if (props.mode === "downgrade") return freePlanDowngradeIllustrationRaw;
    return freePlanUpgradeIllustrationRaw;
});

const extractDomainName = (domainItem: unknown): string => {
    if (typeof domainItem === "string") return domainItem;
    if (
        domainItem &&
        typeof domainItem === "object" &&
        "domain" in domainItem &&
        typeof (domainItem as { domain?: unknown }).domain === "string"
    ) {
        return (domainItem as { domain: string }).domain;
    }
    return "";
};

async function fetchTeamDomains() {
    isLoadingDomains.value = true;
    try {
        const response = await axios.get(route("domains.team-domains"));
        const responseData = response.data ?? {};
        const result = Array.isArray(responseData.result)
            ? responseData.result
            : [];
        domains.value = result
            .map((item) => extractDomainName(item).trim())
            .filter(Boolean);

        const rawCount = Number(responseData.absoluteCount);
        domainsAbsoluteCount.value = Number.isFinite(rawCount)
            ? rawCount
            : domains.value.length;

        if (domains.value.length === 1) {
            selectedDomain.value = domains.value[0];
            return;
        }

        if (!domains.value.includes(selectedDomain.value)) {
            selectedDomain.value = "";
        }
    } catch (error: any) {
        domains.value = [];
        domainsAbsoluteCount.value = 0;
        selectedDomain.value = "";
        create({
            title: "Unable to load domains",
            text:
                error?.response?.data?.message ||
                "Please try again in a moment.",
            type: "error",
        });
    } finally {
        isLoadingDomains.value = false;
    }
}

async function confirm() {
    if (isConfirmDisabled.value) return;

    isSubmitting.value = true;
    try {
        const domainsToKeep = requiresDomainSelection.value
            ? [selectedDomain.value]
            : [];

        await axios.post(route("domains.update-parked-status"), {
            domains: domainsToKeep,
        });

        emit("update:modelValue", false);
        emit("close");
        emit("success", props.mode);
    } catch (error: any) {
        create({
            title: "Unable to switch to Free plan",
            text:
                error?.response?.data?.message ||
                "Please try again or contact support.",
            type: "error",
        });
    } finally {
        isSubmitting.value = false;
    }
}

function close() {
    emit("update:modelValue", false);
    emit("close");
}

function handlePopupClose() {
    emit("close");
}

watch(
    () => props.modelValue,
    (open) => {
        if (open) {
            selectedDomain.value = "";
            fetchTeamDomains();
        }
    },
);
</script>

<style scoped>
:deep(.modal-title) {
    color: #161e2e;
    text-align: center;
    font-family: Inter, sans-serif;
    font-size: 21px;
    font-style: normal;
    font-weight: 600;
    line-height: 142%;
}

:deep(.modal-body) {
    max-width: 433px !important;
    width: 100%;
    padding-left: 0 !important;
    padding-right: 0 !important;
}

.free-plan-popup-body {
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    max-width: 433px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 21px;
}

.free-plan-popup-description {
    color: #18181b;
    text-align: center;
    font-family: Inter, sans-serif;
    font-size: 12.25px;
    font-style: normal;
    font-weight: 400;
    line-height: 142%;
}

.free-plan-popup-select {
    height: 42px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid #d1d5db;
    background: #fff;
    padding: 0 16px;
    font-size: 14px;
    color: #334155;
    outline: none;
    box-shadow: none;
}

.free-plan-popup-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.free-plan-popup-warning {
    width: 100%;
    border-radius: 10px;
    border: 1px solid #fbbf24;
    background: #fef3c7;
    padding: 13px;
    text-align: left;
}

.free-plan-popup-warning-text {
    margin: 0;
    color: #92400e;
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 19.5px;
}

.free-plan-popup-warning-text::before {
    content: "⚠ ";
}

.free-plan-popup-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
}

.free-plan-popup-confirm {
    border-color: transparent !important;
    background: #088bea !important;
    color: #fff !important;
}

.free-plan-popup-confirm:hover {
    color: #f3f4f6 !important;
}

.free-plan-popup-confirm:focus {
    --tw-ring-color: #088bea !important;
}

.free-plan-popup-confirm:disabled {
    background: #d1d5db !important;
    color: #fff !important;
}
</style>
